/**
 * Portal del consumidor — issue #46 (B6.10).
 *
 * El tercer portal de la plataforma: la cuenta con la que el **comprador**
 * guarda los códigos que verificó, se lleva sus datos y se borra.
 *
 * La condición que gobierna esta pantalla es ADR-011:
 *
 *   1. el portal es configurable por tenant y viene **apagado por defecto**;
 *   2. está **prohibido en tenants farmacéuticos**.
 *
 * Guardar qué medicamentos escaneó una persona identificada es un dato de
 * salud —categoría especial bajo GDPR, LGPD y la Ley 6534/2020—. Para vinos o
 * cosmética es fidelización sin riesgo; para un laboratorio es una
 * responsabilidad legal que el laboratorio nos trasladaría por contrato.
 *
 * Por eso en una marca pharma esta sección **no aparece en la navegación**
 * (`AdminNav` no dibuja la solapa) y esta ruta, si alguien pega la URL a
 * mano, explica por qué no existe en vez de mostrar un formulario que el
 * servidor va a rechazar con 404.
 *
 * La sesión del comprador vive **sólo en memoria**. No va a `localStorage` ni
 * a `sessionStorage`: esta pantalla corre en el navegador de la marca y un
 * token de consumidor que sobrevive a un recargado es un token que quedó en
 * una máquina que no es la suya.
 */
import { useState, type ReactNode } from 'react';

import { useI18n } from '@/i18n/useI18n';
import { Badge } from '@/ui/Badge';
import { Card, CardBody, CardHeader } from '@/ui/Card';
import { ErrorState, LoadingState } from '@/ui/states';
import { AdminNav } from '../admin/AdminNav';
import { PortalAccountPanel } from './PortalAccount';
import type { PortalSession } from './portalApi';
import { PortalSignIn } from './PortalSignIn';
import { usePortalConfig } from './usePortalConfig';

/** Los motivos que devuelve `GET /consumer/portal/config`. */
const REASON_KEYS = {
  industry_restricted: 'portal.reason.industry_restricted',
  disabled_by_tenant: 'portal.reason.disabled_by_tenant',
} as const;

export function PortalPage(): ReactNode {
  const { t } = useI18n();
  const config = usePortalConfig();
  const [session, setSession] = useState<PortalSession | null>(null);

  return (
    <div className="mx-auto max-w-4xl">
      <AdminNav />

      <Card>
        <CardHeader
          title={t('portal.title')}
          description={t('portal.description')}
          actions={
            config.data === undefined ? undefined : (
              <Badge tone={config.data.portal_enabled ? 'verified' : 'neutral'}>
                {config.data.portal_enabled ? t('portal.state.enabled') : t('portal.state.disabled')}
              </Badge>
            )
          }
        />

        {config.isPending && <LoadingState rows={3} />}

        {config.isError && (
          <CardBody>
            <ErrorState
              error={config.error}
              onRetry={() => {
                void config.refetch();
              }}
            />
          </CardBody>
        )}

        {config.data !== undefined && (
          <CardBody className="space-y-2">
            <p className="text-sm text-ink">
              <span className="text-ink-muted">{t('portal.brand')}: </span>
              {config.data.brand}
            </p>
            {!config.data.portal_enabled && (
              <>
                <p className="text-sm font-medium text-ink">{t('portal.unavailable.title')}</p>
                <p className="max-w-prose text-sm text-ink-muted">{reasonText(config.data.reason, t)}</p>
              </>
            )}
          </CardBody>
        )}
      </Card>

      {config.data?.portal_enabled === true && (
        <div className="mt-4 space-y-4">
          <Card>
            <CardHeader
              title={t('portal.preview.title')}
              description={t('portal.preview.detail')}
            />
          </Card>

          {session === null ? (
            <PortalSignIn tenant={config.data.tenant} onSession={setSession} />
          ) : (
            <PortalAccountPanel
              session={session}
              onClosed={() => {
                setSession(null);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}

function reasonText(
  reason: string | null | undefined,
  t: ReturnType<typeof useI18n>['t'],
): string {
  if (reason === 'industry_restricted') return t(REASON_KEYS.industry_restricted);
  if (reason === 'disabled_by_tenant') return t(REASON_KEYS.disabled_by_tenant);
  return t('portal.reason.unknown');
}
